import { Title } from '@zorro/design-system/v2/Title';
import { STEPPER_BODY_MAX_WIDTH } from '@zorro/types';
import { Box, Stack } from '@zorro/web/design-system';
import { FormFooter } from '@zorro/web/shared-ui';
import { useAppRouter } from '@zorro/web/ui-utils';
import { ReactNode } from 'react';

import { BulkApplicationBreadcrumbs } from './BulkApplicationBreadcrumbs';
import { useBulkApplication } from './BulkApplicationContext';
import { BulkApplicationStepper } from './BulkApplicationStepper';
import {
  BulkApplicationHandleStepSubmit,
  BulkApplicationStepType,
  getBulkApplicationStepConfig,
} from './BulkApplicationUtils';

type Props = {
  step: BulkApplicationStepType;
  title?: string;
  children: ReactNode;
  onSubmit: BulkApplicationHandleStepSubmit;
  submitLabel?: string;
  isSubmitDisabled?: boolean;
  secondaryLabel?: string;
};

export const BulkApplicationStep = ({
  step,
  title,
  children,
  onSubmit,
  isSubmitDisabled,
  submitLabel = 'Continue',
  secondaryLabel = 'Back',
}: Props) => {
  const { onboardingPeriodIds } = useBulkApplication();
  const { navigateToRoute, isNavigating } = useAppRouter();
  const { nextRoute, prevRoute } = getBulkApplicationStepConfig(step);
  if (onboardingPeriodIds.length === 0 && !isNavigating) {
    navigateToRoute('OPS_ENROLLMENT_MANAGEMENT', []);
  }

  const handleSubmit = async () => {
    const { navigate } = await onSubmit();
    if (navigate) {
      navigateToRoute(nextRoute, []);
    }
  };

  return (
    <>
      <BulkApplicationBreadcrumbs />
      <BulkApplicationStepper step={step} />
      <Box mt="xl" pb={100}>
        <Stack>
          {title && (
            <Title order={2} maw={STEPPER_BODY_MAX_WIDTH}>
              {title}
            </Title>
          )}
          {children}
        </Stack>
      </Box>
      <FormFooter
        primaryLabel={submitLabel}
        primaryButtonProps={{
          onClick: handleSubmit,
          disabled: isSubmitDisabled,
        }}
        secondaryLabel={secondaryLabel}
        secondaryButtonProps={{
          mr: 'auto',
          variant: 'tertiary',
          color: 'zorroCoal.9',
          onClick: async () => {
            navigateToRoute(prevRoute, []);
          },
        }}
      />
    </>
  );
};
